import React, { useState, useEffect, useMemo } from 'react';
import { ChevronLeft, ChevronRight, X, MessageCircle, FileText, Mail, Calendar as CalendarIcon, Share, Pencil, AlertTriangle, RefreshCw } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useData } from '../../context/DataContext';
import { useAuth } from '../../context/AuthContext';
import { pdf } from '@react-pdf/renderer';
import { OfferPDF } from '../offers/OfferPDF';
import LoadingSpinner from '../../components/LoadingSpinner';
import { useSeason } from '../../context/SeasonContext';
import { offerInSeason } from '../../utils/season';
import { API_URL, jsonAuthHeaders, readJsonOrThrow } from '../../utils/api';
import {
  dateKey, todayKey, formatDateRangeTR, formatStay, offerNights, offerMoneyText, offerRoomsText,
  buildOfferMessage, buildOfferEmail, whatsappUrl, mailtoUrl, waPhone,
} from '../../utils/offerShare';
import { fetchMessageSettings, fetchOfferPdfIds, confirmPdfBeforeShare, logOfferShare } from '../../utils/shareSettings';

const WEEKDAYS = [
  ['Pazartesi', 'Pzt'], ['Salı', 'Sal'], ['Çarşamba', 'Çar'], ['Perşembe', 'Per'],
  ['Cuma', 'Cum'], ['Cumartesi', 'Cmt'], ['Pazar', 'Paz'],
];

// Ayar kategorisi → nesne. Okunamazsa {} (PDF ve mesajlar varsayılanlarla çalışır)
const readSettings = async (category) => {
  try {
    const res = await fetch(`${API_URL}/settings/${category}`);
    if (!res.ok) {
      console.error(`Ayarlar (${category}) yüklenemedi: ${res.status}`);
      return {};
    }
    const data = await res.json();
    return data && typeof data === 'object' && !Array.isArray(data) ? data : {};
  } catch (error) {
    console.error(`Ayarlar (${category}) yüklenemedi:`, error);
    return {};
  }
};

export default function CalendarView() {
  const { currentUser } = useAuth();
  const { data, fetchData, loading, errors, refreshData, incrementWrites } = useData();
  const { selectedSeason } = useSeason();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [settings, setSettings] = useState({});   // logos + { bank } (PDF ve e-posta şablonu)
  const [messages, setMessages] = useState({});   // Ayarlar > Mesaj Şablonları ('messages')
  const [pdfIds, setPdfIds] = useState(null);     // sunucuda PDF'i olan teklif no'ları (null = bilinmiyor → uyarı yok)
  const [statusFilter, setStatusFilter] = useState('');
  const [statusSaving, setStatusSaving] = useState(false);
  const [hoveredOffer, setHoveredOffer] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });

  const canWrite = currentUser?.permissions?.write;

  useEffect(() => {
    fetchData('offers');
    fetchData('roomTypes');

    let cancelled = false;
    (async () => {
      const [logos, bank, messageSettings] = await Promise.all([
        readSettings('logos'),
        readSettings('bank'),
        fetchMessageSettings(),
      ]);
      if (cancelled) return;
      setSettings({ ...logos, bank });
      setMessages(messageSettings);
    })();
    // Paylaşım öncesi PDF kontrolü için bir kez yüklenir; alınamazsa null kalır (paylaşım engellenmez)
    fetchOfferPdfIds().then(ids => { if (!cancelled) setPdfIds(ids); });
    return () => { cancelled = true; };
  }, [fetchData]);

  const rooms = useMemo(() => (Array.isArray(data.roomTypes) ? data.roomTypes : []), [data.roomTypes]);

  // Takvimde gösterilecek teklifler + giriş/çıkış günleri ('YYYY-MM-DD', İstanbul günü).
  // API tarihleri ISO zaman damgası döndürür ("2026-12-24T21:00:00.000Z" = 25 Aralık) → dateKey ile çevrilir.
  const calendarOffers = useMemo(() => {
    const list = Array.isArray(data.offers) ? data.offers : [];
    return list
      .filter(offer => offerInSeason(offer, selectedSeason)) // üstteki sezon seçicisi
      .filter(offer => !statusFilter || offer.status === statusFilter)
      .map(offer => ({ offer, from: dateKey(offer.checkIn), to: dateKey(offer.checkOut) }))
      .filter(entry => entry.from && entry.to);
  }, [data.offers, selectedSeason, statusFilter]);

  const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
  const getFirstDayOfMonth = (year, month) => {
      const day = new Date(year, month, 1).getDay();
      return day === 0 ? 6 : day - 1; // Pazartesi başlangıçlı (0=Pzt, ..., 6=Paz)
  };

  const handlePrevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  // Teklifteki oda bilgisine güncel oda özelliklerini ekler (rooms[].id string, room_types.id int → String ile karşılaştır)
  const withCurrentFeatures = (offer) => {
    const currentRoom = rooms.find(r => String(r.id) === String(offer.room?.id));
    return {
      ...offer,
      room: { ...offer.room, features: currentRoom?.features || offer.room?.features }
    };
  };

  // Paylaşım şablonları: Ayarlar > Mesaj Şablonları (boşsa varsayılan), link HER ZAMAN PDF linki (offerLink)
  const shareContext = () => ({
    messages,
    logos: settings,
    bank: settings.bank || {},
    origin: window.location.origin,
  });

  // Paylaşım (WhatsApp/e-posta): PDF'i sunucuda olmayan teklifte önce sorulur; vazgeçilirse paylaşım ve işlem kaydı yok
  const shareOnWhatsApp = (offer, kind, channel) => {
    if (!waPhone(offer.phone)) return alert("Telefon numarası kayıtlı değil.");
    if (!confirmPdfBeforeShare(offer, pdfIds)) return;
    const text = buildOfferMessage(kind, offer, shareContext());
    window.open(whatsappUrl(offer.phone, text), '_blank');
    logOfferShare(offer, channel, currentUser?.token);
  };

  const handleWhatsAppShare = (offer) => shareOnWhatsApp(offer, 'waDetailed', 'WhatsApp (detaylı)');

  const handleWhatsAppLink = (offer) => shareOnWhatsApp(offer, 'waLink', 'WhatsApp (link)');

  const handleEmailShare = (offer) => {
    if (!String(offer.email || '').trim()) return alert("E-posta adresi kayıtlı değil.");
    if (!confirmPdfBeforeShare(offer, pdfIds)) return;
    const { subject, body } = buildOfferEmail(offer, shareContext());
    logOfferShare(offer, 'E-posta', currentUser?.token);
    window.location.href = mailtoUrl(offer.email, subject, body);
  };

  const handleViewPDF = async (offer) => {
    try {
      const blob = await pdf(<OfferPDF offer={withCurrentFeatures(offer)} settings={settings} />).toBlob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      console.error("PDF hatası:", error);
      alert("PDF oluşturulurken bir hata oluştu.");
    }
  };

  const handleMouseEnter = (e, offer) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltipPos({
        x: rect.left + rect.width / 2,
        y: rect.top
    });
    setHoveredOffer(offer);
  };

  const handleStatusChange = async (offer, newStatus) => {
    if (!offer || !newStatus || newStatus === offer.status) return;
    setStatusSaving(true);
    try {
      const response = await fetch(`${API_URL}/offers/${offer.id}`, {
          method: 'PUT',
          headers: jsonAuthHeaders(currentUser?.token),
          body: JSON.stringify({ status: newStatus })
      });
      await readJsonOrThrow(response, 'Durum güncellenemedi.');

      incrementWrites(2); // Güncelleme + Log
      setSelectedOffer(prev => (prev && String(prev.id) === String(offer.id) ? { ...prev, status: newStatus } : prev));

      // İşlem kaydı bilgi amaçlı: hatası güncellemeyi geri almaz
      fetch(`${API_URL}/activityLogs`, {
          method: 'POST',
          headers: jsonAuthHeaders(currentUser?.token),
          body: JSON.stringify({
            action: 'Teklif Durumu Güncellendi',
            description: `${offer.customerName} teklif durumu "${newStatus}" olarak güncellendi.`,
            performedBy: { uid: currentUser?.uid, email: currentUser?.email }
          })
      }).catch(error => console.error("İşlem kaydı yazılamadı:", error));

      refreshData('offers');
    } catch (error) {
      console.error("Durum güncelleme hatası:", error);
      alert(error.message || "Durum güncellenirken bir hata oluştu.");
    } finally {
      setStatusSaving(false);
    }
  };

  const renderCalendar = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const daysInMonth = getDaysInMonth(year, month);
    const firstDay = getFirstDayOfMonth(year, month);
    const today = todayKey();
    const days = [];

    // Boş günler (Önceki ayın son günleri)
    for (let i = 0; i < firstDay; i++) {
        days.push(<div key={`empty-${i}`} className="h-32 bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-700"></div>);
    }

    // Ayın günleri
    for (let day = 1; day <= daysInMonth; day++) {
        const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

        // O gece konaklayan teklifler (giriş günü dahil, çıkış günü hariç)
        const dayOffers = calendarOffers.filter(entry => dateStr >= entry.from && dateStr < entry.to);

        days.push(
            <div key={day} className="h-32 min-w-0 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 p-1 overflow-y-auto relative group hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                <span className={`absolute top-1 right-1 sm:right-2 text-sm font-medium ${
                    dateStr === today
                    ? 'bg-blue-600 text-white w-6 h-6 flex items-center justify-center rounded-full'
                    : 'text-gray-700 dark:text-gray-300'
                }`}>{day}</span>

                <div className="mt-6 space-y-1">
                    {dayOffers.map(({ offer }) => (
                        <div
                            key={offer.id}
                            onMouseEnter={(e) => handleMouseEnter(e, offer)}
                            onMouseLeave={() => setHoveredOffer(null)}
                            onClick={() => setSelectedOffer(withCurrentFeatures(offer))}
                            className={`text-[10px] px-1.5 py-1 rounded cursor-pointer truncate font-medium transition-all hover:opacity-80 ${
                                offer.status === 'Onaylandı' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300 border border-green-200 dark:border-green-800' :
                                offer.status === 'Onaylanmadı' ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300 border border-red-200 dark:border-red-800' :
                                'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300 border border-yellow-200 dark:border-yellow-800'
                            }`}
                        >
                            {offer.customerName}
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    return days;
  };

  // null = henüz okunmadı. Okuma başarısızsa DataContext önceki listeyi (yoksa []) korur ve errors.offers'ı doldurur.
  if (loading.offers || !Array.isArray(data.offers)) return <LoadingSpinner />;

  const offersError = errors?.offers || '';
  // Elde hiç teklif yokken hata: boş takvim "hiç rezervasyon yok" gibi okunur → takvim yerine uyarı
  const offersUnavailable = !!offersError && data.offers.length === 0;
  const retryOffers = () => refreshData('offers');

  const selectedContact = selectedOffer ? [selectedOffer.phone, selectedOffer.email].filter(v => String(v || '').trim()).join(' • ') : '';
  const selectedNights = selectedOffer ? offerNights(selectedOffer) : 0;

  return (
    <div className="h-full flex flex-col">
      <div className="flex flex-col xl:flex-row justify-between items-center mb-6 gap-4">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
            <CalendarIcon size={28} className="text-blue-600" />
            Takvim
        </h1>

        <div className="flex gap-2 overflow-x-auto max-w-full pb-2 xl:pb-0">
          {['', 'Onay Bekliyor', 'Onaylandı', 'Onaylanmadı'].map((status) => (
            <button
              key={status || 'all'}
              onClick={() => setStatusFilter(status)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors border ${
                statusFilter === status
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              {status === '' ? 'Tümü' : status}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-4 bg-white dark:bg-gray-800 p-1 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
            <button onClick={handlePrevMonth} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg text-gray-600 dark:text-gray-300" aria-label="Önceki ay">
                <ChevronLeft size={20} />
            </button>
            <span className="text-lg font-semibold text-gray-800 dark:text-white min-w-[140px] text-center">
                {currentDate.toLocaleDateString('tr-TR', { month: 'long', year: 'numeric' })}
            </span>
            <button onClick={handleNextMonth} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg text-gray-600 dark:text-gray-300" aria-label="Sonraki ay">
                <ChevronRight size={20} />
            </button>
        </div>
      </div>

      {offersError && !offersUnavailable && (
        <div role="alert" className="mb-4 px-4 py-3 rounded-lg border border-red-200 bg-red-50 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300 flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex items-start gap-2 flex-1 min-w-0">
            <AlertTriangle size={18} className="mt-0.5 shrink-0" />
            <p className="min-w-0 break-words">
              <span className="font-medium">Teklifler güncellenemedi — takvim son başarılı yüklemeyi gösteriyor.</span>{' '}
              <span className="opacity-90">{offersError}</span>
            </p>
          </div>
          <button
            type="button"
            onClick={retryOffers}
            className="self-start sm:self-auto shrink-0 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-600"
          >
            <RefreshCw size={14} /> Tekrar dene
          </button>
        </div>
      )}

      {offersUnavailable ? (
        <div role="alert" className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-red-200 dark:border-red-800 p-6 sm:p-10 text-center">
          <AlertTriangle size={32} className="mx-auto mb-3 text-red-500 dark:text-red-400" />
          <p className="font-medium text-gray-800 dark:text-gray-100">Teklifler yüklenemedi.</p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 break-words">{offersError}</p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Takvim, teklifler alınamadığı için gösterilemiyor.</p>
          <button
            type="button"
            onClick={retryOffers}
            className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-600"
          >
            <RefreshCw size={16} /> Tekrar dene
          </button>
        </div>
      ) : (
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden flex-1 flex flex-col">
        {/* Gün Başlıkları (dar ekranda kısaltılmış) */}
        <div className="grid grid-cols-7 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
            {WEEKDAYS.map(([long, short]) => (
                <div key={long} className="py-3 text-center text-xs sm:text-sm font-semibold text-gray-600 dark:text-gray-400 truncate" title={long}>
                    <span className="hidden sm:inline">{long}</span>
                    <span className="sm:hidden">{short}</span>
                </div>
            ))}
        </div>

        {/* Takvim Grid */}
        <div className="grid grid-cols-7 flex-1 auto-rows-fr">
            {renderCalendar()}
        </div>
      </div>
      )}

      {/* Tooltip */}
      {hoveredOffer && (
        <div
            className="fixed z-[100] bg-gray-900 text-white text-xs rounded p-2 shadow-xl pointer-events-none transform -translate-x-1/2 -translate-y-full"
            style={{ left: tooltipPos.x, top: tooltipPos.y - 8 }}
        >
            <div className="text-center min-w-[150px] max-w-[240px]">
                <p className="font-bold text-white mb-1">{hoveredOffer.customerName}</p>
                <p className="text-gray-300 mb-0.5">{offerRoomsText(hoveredOffer)}</p>
                <p className="text-gray-400 text-[10px] mb-1">
                    {formatStay(hoveredOffer)}
                </p>
                <p className="font-bold text-blue-300 border-t border-gray-700 pt-1 mt-1">
                    {offerMoneyText(hoveredOffer)}
                </p>
            </div>
            {/* Arrow */}
            <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
        </div>
      )}

      {/* Detay Modalı */}
      {selectedOffer && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setSelectedOffer(null)}>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-lg w-full max-h-[calc(100vh-2rem)] overflow-y-auto animate-in zoom-in-95 duration-200" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center p-5 border-b dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
              <h3 className="text-lg font-bold text-gray-800 dark:text-white">Teklif Detayı</h3>
              <button onClick={() => setSelectedOffer(null)} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200" aria-label="Kapat">
                <X size={20} />
              </button>
            </div>

            <div className="p-6 space-y-4">
                <div className="min-w-0">
                    <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Müşteri</label>
                    <p className="text-gray-900 dark:text-white font-medium break-words">{selectedOffer.customerName}</p>
                    {selectedContact && (
                        <p className="text-sm text-gray-500 dark:text-gray-400 break-all">{selectedContact}</p>
                    )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="min-w-0">
                        <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Giriş - Çıkış</label>
                        <p className="text-gray-900 dark:text-white text-sm">
                            {formatDateRangeTR(selectedOffer.checkIn, selectedOffer.checkOut) || '-'}
                        </p>
                        {selectedNights > 0 && (
                            <span className="text-xs bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-1.5 py-0.5 rounded mt-1 inline-block">
                                {selectedNights} Gece
                            </span>
                        )}
                    </div>
                    <div className="min-w-0">
                        <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Oda & Tutar</label>
                        <p className="text-gray-900 dark:text-white text-sm break-words">{offerRoomsText(selectedOffer) || '-'}</p>
                        <p className="font-bold text-blue-600 dark:text-blue-400">
                            {offerMoneyText(selectedOffer)}
                        </p>
                    </div>
                </div>

                <div>
                    <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Durum</label>
                    <div className="mt-1">
                        {canWrite ? (
                            <select
                                value={selectedOffer.status}
                                disabled={statusSaving}
                                onChange={(e) => handleStatusChange(selectedOffer, e.target.value)}
                                className={`w-full p-2 text-sm border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white disabled:opacity-60 ${
                                    selectedOffer.status === 'Onaylandı' ? 'bg-green-50 border-green-200 text-green-800 dark:bg-green-900/20 dark:border-green-800 dark:text-green-300' :
                                    selectedOffer.status === 'Onaylanmadı' ? 'bg-red-50 border-red-200 text-red-800 dark:bg-red-900/20 dark:border-red-800 dark:text-red-300' :
                                    'bg-yellow-50 border-yellow-200 text-yellow-800 dark:bg-yellow-900/20 dark:border-yellow-800 dark:text-yellow-300'
                                }`}
                            >
                                <option value="Onay Bekliyor">Onay Bekliyor</option>
                                <option value="Onaylandı">Onaylandı</option>
                                <option value="Onaylanmadı">Onaylanmadı</option>
                            </select>
                        ) : (
                            <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                                selectedOffer.status === 'Onaylandı' ? 'bg-green-100 text-green-800' :
                                selectedOffer.status === 'Onaylanmadı' ? 'bg-red-100 text-red-800' :
                                'bg-yellow-100 text-yellow-800'
                            }`}>
                                {selectedOffer.status}
                            </span>
                        )}
                    </div>
                </div>
            </div>

            {selectedOffer.notes && (
                <div className="px-6 pb-4">
                    <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Notlar</label>
                    <div className="mt-1 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-100 dark:border-gray-700">
                        <p className="text-sm text-gray-600 dark:text-gray-300 whitespace-pre-wrap break-words">{selectedOffer.notes}</p>
                    </div>
                </div>
            )}

            <div className="p-5 border-t dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 flex justify-between items-center gap-2">
                <div className="flex gap-2 flex-wrap">
                    <button onClick={() => handleWhatsAppShare(selectedOffer)} className="flex items-center gap-2 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/30 px-3 py-2 rounded-lg transition-colors" title="WhatsApp (detaylı mesaj)">
                        <MessageCircle size={20} /> <span className="text-sm font-medium hidden sm:inline">WhatsApp</span>
                    </button>
                    <button onClick={() => handleWhatsAppLink(selectedOffer)} className="flex items-center gap-2 text-green-700 hover:bg-green-50 dark:hover:bg-green-900/30 px-3 py-2 rounded-lg transition-colors" title="WhatsApp (yalnızca link)">
                        <Share size={20} /> <span className="text-sm font-medium hidden sm:inline">WP Link</span>
                    </button>
                    <button onClick={() => handleViewPDF(selectedOffer)} className="flex items-center gap-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 px-3 py-2 rounded-lg transition-colors" title="PDF">
                        <FileText size={20} /> <span className="text-sm font-medium hidden sm:inline">PDF</span>
                    </button>
                    <button onClick={() => handleEmailShare(selectedOffer)} className="flex items-center gap-2 text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/30 px-3 py-2 rounded-lg transition-colors" title="E-posta">
                        <Mail size={20} /> <span className="text-sm font-medium hidden sm:inline">E-posta</span>
                    </button>
                    {canWrite && (
                    <Link
                        to={`/offers/edit/${selectedOffer.id}`}
                        className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-600 px-3 py-2 rounded-lg transition-colors"
                        title="Düzenle"
                    >
                        <Pencil size={20} />
                        <span className="text-sm font-medium hidden sm:inline">Düzenle</span>
                    </Link>
                    )}
                </div>
                <button onClick={() => setSelectedOffer(null)} className="shrink-0 px-4 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors text-gray-700 dark:text-gray-200">
                    Kapat
                </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
